/**
 * Fork Yeah meal builder — validates picks, syncs SKU/qty props, gates ATC / Reserve.
 */
class MealBuilder extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLButtonElement | null} */
  #submitButton = null;

  connectedCallback() {
    this.#resolveForm();
    this.dataset.fyDateReady = 'false';
    this.#setMealControlsLocked(true);
    this.#loadInventory();

    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onChange);
    document.addEventListener('fy:pickup-day', this.#onPickupDay);
    document.addEventListener('click', this.#onDocumentClick, true);

    if (this.#form) {
      this.#form.addEventListener('submit', this.#onSubmit, true);
    }

    requestAnimationFrame(() => {
      this.#resolveForm();
      this.#checkPools();
      this.#syncDuplicateOptions();
      this.#syncSkuQtyProps();
      this.#updateValidity();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onChange);
    document.removeEventListener('fy:pickup-day', this.#onPickupDay);
    document.removeEventListener('click', this.#onDocumentClick, true);
    if (this.#form) {
      this.#form.removeEventListener('submit', this.#onSubmit, true);
    }
  }

  #resolveForm() {
    const formId = this.dataset.formId;
    let form = formId ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId)) : null;

    if (!form) {
      form = this.closest('.shopify-section')?.querySelector(
        'product-form-component form[data-type="add-to-cart-form"]'
      ) || null;
    }

    if (form && form !== this.#form) {
      if (this.#form) {
        this.#form.removeEventListener('submit', this.#onSubmit, true);
      }
      this.#form = form;
      this.#form.addEventListener('submit', this.#onSubmit, true);
    }

    if (this.#form?.getAttribute('id')) {
      this.querySelectorAll('[name^="properties["]').forEach((field) => {
        field.setAttribute('form', this.#form.getAttribute('id'));
      });
    }

    this.#submitButton =
      this.#form?.querySelector('.fy-reserve-cta, [type="submit"], button[name="add"], .add-to-cart-button') ||
      (formId ? document.querySelector(`button[form="${formId}"]`) : null) ||
      null;
  }

  #onChange = () => {
    this.#refreshInventoryOptions();
    this.#syncDuplicateOptions();
    this.#syncSkuQtyProps();
    this.#updateValidity();
    this.#clearError();
  };

  #onDocumentClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement) || this.#form?.contains(button)) return;
    if (!/add to cart/i.test(button.textContent || '')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.#showError('Choose the pickup date and complete the meal below.');
  };

  #gateExternalButtons() {
    document.querySelectorAll('button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement) || this.#form?.contains(button)) return;
      if (!/add to cart/i.test(button.textContent || '')) return;
      button.disabled = true;
      button.dataset.fyMealExternal = 'true';
      button.title = 'Choose a date and build this meal below';
    });
  }

  #onPickupDay = () => {
    this.#resolveForm();
    this.#updateValidity();
  };

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (this.prepareForSubmit()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showError(this.#incompleteMessage());
  };

  // Called by the product form immediately before it captures the line data.
  prepareForSubmit() {
    this.#resolveForm();
    this.#syncSkuQtyProps();
    const valid = Boolean(this.#form) && this.#isValid();
    if (!valid) this.#showError(this.#incompleteMessage());
    return valid;
  }

  #needsPickupDay() {
    return Boolean(this.#form?.closest('product-form-component[data-fy-need-day]'));
  }

  #hasPickupDaySelected() {
    const checked = document.querySelector('.fy-pdp-day__radio:checked');
    if (checked instanceof HTMLInputElement && checked.value) return true;
    const prop = document.querySelector('[data-fy-pickup-property]');
    if (prop instanceof HTMLInputElement && prop.value.trim()) return true;
    return false;
  }

  #cornbreadOk() {
    return this.dataset.cornbreadOk === 'true';
  }

  #incompleteMessage() {
    if (!this.#dateSelect()?.value) {
      return 'Choose a pickup date before building your meal.';
    }
    if (!this.#cornbreadOk()) {
      return 'Honey Butter Cornbread is sold out — meals aren’t available until it’s restocked.';
    }
    const sauceOpts = this.#countRealOptions(this.#sauceSelect());
    if (sauceOpts < 1) {
      return 'No sauces left today — check back next smoke.';
    }
    if (this.#needsPickupDay()) {
      return 'Pick every meat, side, sauce, and utensil choice before reserving.';
    }
    return 'Pick every meat, side, sauce, and utensil choice before adding to cart.';
  }

  #meatSelects() {
    return /** @type {HTMLSelectElement[]} */ ([...this.querySelectorAll('select[data-fy-slot="meat"]')]);
  }

  #sideSelects() {
    return /** @type {HTMLSelectElement[]} */ ([...this.querySelectorAll('select[data-fy-slot="side"]')]);
  }

  #sauceSelect() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-fy-slot="sauce"]'));
  }

  #checkPools() {
    const meatSlots = Number(this.dataset.meatSlots || 0);
    const sideSlots = Number(this.dataset.sideSlots || 0);
    const meatOptions = this.#countRealOptions(this.#meatSelects()[0]);
    const sideOptions = this.#countRealOptions(this.#sideSelects()[0]);
    const sauceOptions = this.#countRealOptions(this.#sauceSelect());

    this.#toggleEmpty('meats', meatOptions < (this.dataset.allowDupMeats === 'true' ? 1 : meatSlots));
    this.#toggleEmpty('sides', sideOptions < (this.dataset.allowDupSides === 'true' ? 1 : sideSlots));
    this.#toggleEmpty('sauce', sauceOptions < 1);
    this.#toggleEmpty('cornbread', !this.#cornbreadOk());
  }

  /**
   * @param {string} key
   * @param {boolean} show
   */
  #toggleEmpty(key, show) {
    const el = this.querySelector(`[data-fy-empty="${key}"]`);
    if (!(el instanceof HTMLElement)) return;
    el.hidden = !show;
    el.classList.toggle('hidden', !show);
  }

  /**
   * @param {HTMLSelectElement | null | undefined} select
   */
  #countRealOptions(select) {
    if (!select) return 0;
    return [...select.options].filter((opt) => opt.value).length;
  }

  #syncDuplicateOptions() {
    const allowDupMeats = this.dataset.allowDupMeats === 'true';
    const allowDupSides = this.dataset.allowDupSides === 'true';

    if (!allowDupMeats) this.#disableTakenOptions(this.#meatSelects());
    if (!allowDupSides) this.#disableTakenOptions(this.#sideSelects());
  }

  /**
   * @param {HTMLSelectElement[]} selects
   */
  #disableTakenOptions(selects) {
    const selected = new Set(selects.map((s) => s.value).filter(Boolean));

    for (const select of selects) {
      const current = select.value;
      for (const option of select.options) {
        if (!option.value) continue;
        option.disabled = option.dataset.fyInventoryDisabled === 'true' || (selected.has(option.value) && option.value !== current);
      }
    }
  }

  #syncSkuQtyProps() {
    for (const select of [...this.#meatSelects(), ...this.#sideSelects()]) {
      const slot = select.dataset.fySlot;
      const index = select.dataset.fyIndex;
      const opt = select.selectedOptions[0];
      const sku = opt?.dataset?.fySku || '';
      const qty = opt?.dataset?.fyQty || '';
      const skuInput = this.querySelector(
        `input[data-fy-prop-sku="${slot}"][data-fy-index="${index}"]`
      );
      const qtyInput = this.querySelector(
        `input[data-fy-prop-qty="${slot}"][data-fy-index="${index}"]`
      );
      if (skuInput instanceof HTMLInputElement) skuInput.value = select.value ? sku : '';
      if (qtyInput instanceof HTMLInputElement) qtyInput.value = select.value ? qty : '';
    }

    const sauce = this.#sauceSelect();
    if (sauce) {
      const opt = sauce.selectedOptions[0];
      const sku = opt?.dataset?.fySku || '';
      const qty = opt?.dataset?.fyQty || '';
      const skuInput = this.querySelector('input[data-fy-prop-sku="sauce"]');
      const qtyInput = this.querySelector('input[data-fy-prop-qty="sauce"]');
      if (skuInput instanceof HTMLInputElement) skuInput.value = sauce.value ? sku : '';
      if (qtyInput instanceof HTMLInputElement) qtyInput.value = sauce.value ? qty : '';
    }

    const includesSku = this.querySelector('input[data-fy-prop-sku="includes"]');
    const includesQty = this.querySelector('input[data-fy-prop-qty="includes"]');
    if (includesSku instanceof HTMLInputElement && includesQty instanceof HTMLInputElement) {
      if (this.#cornbreadOk()) {
        includesSku.value = this.dataset.cornbreadSku || includesSku.value;
        includesQty.value = includesQty.value || '1';
      } else {
        includesSku.value = '';
        includesQty.value = '';
      }
    }
  }

  #isValid() {
    if (!this.#cornbreadOk()) return false;
    if (!this.#dateSelect()?.value) return false;

    const meatSlots = Number(this.dataset.meatSlots || 0);
    const sideSlots = Number(this.dataset.sideSlots || 0);
    const meats = this.#meatSelects().map((s) => s.value).filter(Boolean);
    const sides = this.#sideSelects().map((s) => s.value).filter(Boolean);
    const sauce = this.#sauceSelect()?.value || '';

    if (meats.length < meatSlots || sides.length < sideSlots || !sauce) return false;

    if (this.dataset.allowDupMeats !== 'true' && new Set(meats).size !== meats.length) return false;
    if (this.dataset.allowDupSides !== 'true' && new Set(sides).size !== sides.length) return false;

    const meatOptions = this.#countRealOptions(this.#meatSelects()[0]);
    const sideOptions = this.#countRealOptions(this.#sideSelects()[0]);
    const sauceOptions = this.#countRealOptions(this.#sauceSelect());
    if (meatOptions < (this.dataset.allowDupMeats === 'true' ? 1 : meatSlots) || sideOptions < (this.dataset.allowDupSides === 'true' ? 1 : sideSlots) || sauceOptions < 1) return false;

    for (const select of this.#meatSelects()) {
      const idx = select.dataset.fyIndex;
      const skuEl = this.querySelector(`input[data-fy-prop-sku="meat"][data-fy-index="${idx}"]`);
      if (!(skuEl instanceof HTMLInputElement) || !skuEl.value) return false;
    }
    for (const select of this.#sideSelects()) {
      const idx = select.dataset.fyIndex;
      const skuEl = this.querySelector(`input[data-fy-prop-sku="side"][data-fy-index="${idx}"]`);
      if (!(skuEl instanceof HTMLInputElement) || !skuEl.value) return false;
    }
    const sauceSku = this.querySelector('input[data-fy-prop-sku="sauce"]');
    if (!(sauceSku instanceof HTMLInputElement) || !sauceSku.value) return false;
    const includesSku = this.querySelector('input[data-fy-prop-sku="includes"]');
    if (!(includesSku instanceof HTMLInputElement) || !includesSku.value) return false;

    const requiredValues = ['Utensils', 'Includes', 'Portion'];
    for (const name of requiredValues) {
      const field = this.querySelector('[name="properties[' + name + ']"]');
      if (!field || !field.value?.trim() || field.disabled) return false;
    }
    for (const field of this.querySelectorAll('[data-fy-prop-qty]')) {
      const qty = Number(field.value);
      if (!Number.isFinite(qty) || qty <= 0) return false;
    }
    for (const field of this.querySelectorAll('[name^="properties["]')) {
      if (field.disabled || field.form !== this.#form) return false;
    }
    return true;
  }

  /**
   * @param {HTMLButtonElement} btn
   * @param {boolean} valid
   * @param {boolean} dayReady
   */
  #applyButtonGate(btn, valid, dayReady) {
    const needDay = this.#needsPickupDay();
    const canEnable = valid && (!needDay || dayReady);

    if (canEnable) {
      if (btn.dataset.fyMealGated === 'true' || !btn.disabled) {
        btn.removeAttribute('disabled');
        delete btn.dataset.fyMealGated;
      }
      return;
    }

    if (!valid) {
      btn.setAttribute('disabled', 'disabled');
      btn.dataset.fyMealGated = 'true';
      return;
    }

    if (btn.dataset.fyMealGated === 'true') {
      btn.setAttribute('disabled', 'disabled');
    }
  }

  #updateValidity() {
    this.#resolveForm();
    this.#gateExternalButtons();
    this.#checkPools();
    this.#syncSkuQtyProps();
    const valid = this.#isValid();
    this.toggleAttribute('data-valid', valid);
    const dayReady = this.#hasPickupDaySelected();

    if (this.#submitButton) {
      const btn = this.#submitButton;
      const needDay = this.#needsPickupDay();
      const controllable =
        needDay ||
        !btn.hasAttribute('disabled') ||
        btn.dataset.fyMealGated === 'true';

      if (controllable) {
        this.#applyButtonGate(btn, valid, dayReady);
      }
    }

    const formId = this.dataset.formId;
    if (formId) {
      document.querySelectorAll(`button[form="${formId}"], [form="${formId}"][type="submit"]`).forEach((el) => {
        if (!(el instanceof HTMLButtonElement)) return;
        this.#applyButtonGate(el, valid, dayReady);
      });
    }
  }

  /**
   * @param {string} message
   */

  #dateSelect() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-fy-inventory-date]'));
  }

  #setMealControlsLocked(locked) {
    this.dataset.fyDateReady = locked ? 'false' : 'true';
    this.querySelectorAll('select[data-fy-slot], select[name="properties[Utensils]"]').forEach((field) => {
      field.disabled = locked;
    });
  }

  async #loadInventory() {
    const dateSelect = this.#dateSelect();
    const status = this.querySelector('[data-fy-inventory-status]');
    if (!dateSelect) return;
    this.#setMealControlsLocked(true);
    try {
      const [inventoryResponse, cartResponse] = await Promise.all([
        fetch('/apps/pit-inventory', { cache: 'no-store', credentials: 'same-origin' }),
        fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin' })
      ]);
      if (!inventoryResponse.ok) throw new Error('Inventory is unavailable');
      const inventory = await inventoryResponse.json();
      const cart = cartResponse.ok ? await cartResponse.json() : { items: [] };
      this._fyDates = inventory.dates || {};
      this._fyCartUsage = this.#readCartUsage(cart.items || []);
      dateSelect.innerHTML = '<option value="">Select a pickup date…</option>';
      Object.keys(this._fyDates).sort().forEach((iso) => {
        const option = document.createElement('option');
        option.value = iso;
        option.textContent = new Date(iso + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
        dateSelect.append(option);
      });
      dateSelect.disabled = false;
      dateSelect.addEventListener('change', this.#onInventoryDate);
      if (Object.keys(this._fyDates).length === 0) {
        dateSelect.disabled = true;
        if (status) status.textContent = 'No pickup dates have inventory loaded yet.';
      }
    } catch (error) {
      dateSelect.innerHTML = '<option value="">Pickup dates temporarily unavailable</option>';
      dateSelect.disabled = true;
      if (status) status.textContent = 'We could not check today’s pit inventory. Please try again.';
    }
    this.#updateValidity();
  }

  #readCartUsage(items) {
    const usage = {};
    for (const item of items) {
      const props = item.properties || {};
      const date = props['_Pit Inventory Date'] || props['Pickup Date'];
      if (!date) continue;
      usage[date] ||= {};
      for (let i = 1; i <= 3; i += 1) {
        const key = this.#inventoryKey(props['Meat ' + i] || props['_Meat ' + i + ' SKU']);
        if (key) usage[date][key] = (usage[date][key] || 0) + Number(item.quantity || 1);
      }
    }
    return usage;
  }

  #inventoryKey(value) {
    const text = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (text.includes('brisket')) return 'brisket';
    if (text.includes('tritip')) return 'triTip';
    if (text.includes('pulledpork')) return 'pulledPork';
    if (text.includes('rib')) return 'ribs';
    if (text.includes('chicken')) return 'chicken';
    return '';
  }

  #onInventoryDate = async () => {
    const dateSelect = this.#dateSelect();
    const iso = dateSelect?.value || '';
    const human = dateSelect?.selectedOptions[0]?.textContent || '';
    const pickup = this.querySelector('[data-fy-pickup-property]');
    const inventory = this.querySelector('[data-fy-inventory-property]');
    if (pickup instanceof HTMLInputElement) pickup.value = human;
    if (inventory instanceof HTMLInputElement) inventory.value = iso;
    this.#setMealControlsLocked(!iso);
    this.#refreshInventoryOptions();
    this.#syncDuplicateOptions();
    this.#syncSkuQtyProps();
    const status = this.querySelector('[data-fy-inventory-status]');
    if (status) status.textContent = iso ? 'Building for ' + human + '. Sold-out meats are disabled.' : 'Select a date before choosing your meal.';
    if (iso) {
      const attrs = { 'Pickup day': human, 'Pickup Date': iso, '_Pit Inventory Date': iso };
      if (window.FYBirdPickup?.attributesForDay) Object.assign(attrs, window.FYBirdPickup.attributesForDay(human));
      fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: attrs }) }).catch(() => {});
      document.dispatchEvent(new CustomEvent('fy:pickup-day', { detail: { day: human, iso } }));
    }
    this.#updateValidity();
  };

  #refreshInventoryOptions() {
    const iso = this.#dateSelect()?.value || '';
    const day = this._fyDates?.[iso] || {};
    const used = this._fyCartUsage?.[iso] || {};
    const selects = this.#meatSelects();
    for (const select of selects) {
      for (const option of select.options) {
        const key = this.#inventoryKey(option.value || option.dataset.fySku);
        if (!key) continue;
        const selectedElsewhere = selects.filter((other) => other !== select && this.#inventoryKey(other.value) === key).length;
        const remaining = Number(day[key]?.remainingParts || 0);
        const unavailable = !iso || (Number(used[key] || 0) + selectedElsewhere + 1 > remaining);
        option.dataset.fyInventoryDisabled = unavailable ? 'true' : 'false';
        option.disabled = unavailable;
        option.textContent = option.textContent.replace(/ — sold out$/, '') + (unavailable && iso ? ' — sold out' : '');
      }
      if (select.selectedOptions[0]?.dataset.fyInventoryDisabled === 'true') select.value = '';
    }
  }

  #showError(message) {
    const el = this.querySelector('[data-fy-error]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = message;
    el.hidden = false;
  }

  #clearError() {
    const el = this.querySelector('[data-fy-error]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = '';
    el.hidden = true;
  }
}

if (!customElements.get('meal-builder')) {
  customElements.define('meal-builder', MealBuilder);
}


(function(){
  function addBothSauces(){
      document.querySelectorAll('select[id^="fy-meal-sauce-"]').forEach(function(select){
            if(Array.prototype.some.call(select.options,function(option){return option.value==='Both Sauces';})) return;
                  var option=select.options[1]?select.options[1].cloneNode(true):document.createElement('option');option.disabled=false;
                        option.value='Both Sauces';
                              option.textContent='Both Sauces — ½ Original + ½ Forking Hot';
                                    select.appendChild(option);
                                        });
                                          }
                                            if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',addBothSauces,{once:true});else addBothSauces();
                                              [250,500,1000,2000].forEach(function(delay){setTimeout(addBothSauces,delay);});
                                              })();
                                              
